import { put, takeEvery, select } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import {
  savePrinter,
  fetchAllPrinter,
  deletePrinter,
  emptyPrinters,
  getUnprintedTickets,
  getLabelPrintStatus,
  clearDefaultPrinter,
} from "../../slices/PrinterSlice";
import { printLabelSlice } from "../../slices/TableViewSlice";
import { notifyError } from "../../../effects-bridge";
import { pos_errormap } from "../../../utils/posErrors";
import { METHODS } from "../../../data/constants";
import { savePrinterDetails, fetchAllPrinterDetails, updateLabelPrintStatus } from "./shared";

export * from "./shared";

/**
 * WEB half of `redux-store/saga/printer`. `shared.ts` owns the two identical workers; the four
 * below diverge from mobile's — `updateLabelPrintStatus` least of all now: mobile runs this exact
 * worker as of 2026-09-16, for the print engine's label polling.
 *
 * ⚠️ Every core import names the VARIANT, not the folder (hazard 6/16): this file knows its
 * platform, so it binds to its own axios instance, endpoint map, slices and error map.
 * `pos_errormap` in particular is variant-defined on both sides with different contents.
 */

/**
 * ⚠️ **Divergent — the `REST_PRINTER_HAS_REFERENCES` toast is WEB ONLY.** Mobile deletes without
 * telling the user why a delete was refused. Preserved; see `index.native.ts`.
 *
 * The toast is the injected `notifyError` (core cannot import UI); both apps wire it to their own
 * `ToastNotifyError` in `bootstrapCoreBridges.ts`.
 */
function* deletePrinterDetails(actions: any): Generator<any, void, any> {
  try {
    const defaultPrinterDetails = yield select((state: any) => state.printerStore?.defaultPrinter);

    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.PRINTER.deletePrinter(actions?.payload),
    });
    if (response?.code === "REST_PRINTER_HAS_REFERENCES") {
      notifyError(pos_errormap[response?.code]);
    }
    yield put(emptyPrinters({}));
    yield put(fetchAllPrinter({}));
    if (defaultPrinterDetails?.mid === actions?.payload) {
      yield put(clearDefaultPrinter());
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * ⚠️ Mobile's twin converged onto this body 2026-09-12 — same endpoint, same
 * `getTicket`/`getItemLabel` toggles, same `{ ticketSales, itemLabelSales }` response. Two
 * differences remain, both real: this one passes `skipGlobalLoader` where mobile passes
 * `hideLoader` (different interceptors read different flag names), and this one calls the
 * callback with optional chaining where mobile `yield`s it.
 *
 * `fetchUnprinted` moved to the shared endpoint map in that change; it is no longer web-only.
 */
function* fetchUnprintedTickets(actions: any): Generator<any, void, any> {
  const { dataToSend, callback = () => {}, errorCallback = () => {}, ticketsToggle, labelsToggle } =
    actions.payload || {};
  try {
    const queryParams = [];

    if (ticketsToggle) queryParams.push("getTicket=1");
    if (labelsToggle) queryParams.push("getItemLabel=1");

    const queryString = queryParams.join("&");
    const response = yield api.request({
      method: METHODS.POST,
      url: ENDPOINT.PRINTER.fetchUnprinted(queryString),
      data: dataToSend || {},
      skipGlobalLoader: true,
      // `skipGlobalLoader` is the apps' own axios flag, read by apiService's interceptors; it is
      // not in `AxiosRequestConfig`, so the cast is required once the file becomes `.ts`.
      // Same shape as `redux-store/saga/commonSaga`, which passes the identical flag.
    } as any);
    callback?.(response);
  } catch (error) {
    errorCallback?.(error);
  }
}

/** ⚠️ Divergent only in `skipGlobalLoader`, which mobile's copy does not send. */
function* fetchLabelPrintStatus(actions: any): Generator<any, void, any> {
  const mid = actions.payload?.mid;
  try {
    const response = yield api.request({
      method: METHODS.POST,
      url: ENDPOINT.PRINTER.getLabelPrintStatusApi(mid),
      skipGlobalLoader: true,
      // `skipGlobalLoader` is the apps' own axios flag, read by apiService's interceptors; it is
      // not in `AxiosRequestConfig`, so the cast is required once the file becomes `.ts`.
      // Same shape as `redux-store/saga/commonSaga`, which passes the identical flag.
    } as any);
    yield actions.payload.callback?.(response);
  } catch (error) {
    yield actions.payload.errorCallback?.(error);
    console.log(error);
  }
}

/** SIX watchers, and mobile's `root` now has the same six. */
export default function* root() {
  yield takeEvery(savePrinter.type, savePrinterDetails);
  yield takeEvery(fetchAllPrinter.type, fetchAllPrinterDetails);
  yield takeEvery(deletePrinter.type, deletePrinterDetails);
  yield takeEvery(getUnprintedTickets.type, fetchUnprintedTickets);
  yield takeEvery(getLabelPrintStatus.type, fetchLabelPrintStatus);
  yield takeEvery(printLabelSlice.type, updateLabelPrintStatus);
}
